import { ValidateHubDefiAddressResponse } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { useCampaign } from "../campaign/useCampaign";
import { useTradeForm } from "../trading/form/useTradeForm";
import { useHubUser } from "./useHubUser";
import { CAMPAIGN_STATUS } from "@/components/features/campaigns/types";
import { useApi } from "@/hooks/core/useApi";
import { useLoading } from "@/store/loading/loading";
import { QueryClient, useQuery } from "@tanstack/react-query";

type UseValidateHubDefiAddressProps = {
  campaignName: string;
};

const VALIDATE_HUB_DEFI_ADDRESS_QUERY_KEY = "validateHubDefiAddress";
const DEFAULT_VALIDATION_RESPONSE: ValidateHubDefiAddressResponse = {
  isValid: true,
  defiAddresses: [],
};

export function invalidateHubDefiAddress(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: [VALIDATE_HUB_DEFI_ADDRESS_QUERY_KEY] });
}

export function useValidateHubDefiAddress({ campaignName }: UseValidateHubDefiAddressProps): {
  validationResponse: ValidateHubDefiAddressResponse | undefined;
  isLoading: boolean;
} {
  const { hubUser, auth, authenticated } = useHubUser();
  const { tradeForm } = useTradeForm();
  const { campaign } = useCampaign(campaignName);
  const apiHelper = useApi();
  const { addBigPendingRequest, removeBigPendingRequest } = useLoading((s) => ({
    addBigPendingRequest: s.addBigPendingRequest,
    removeBigPendingRequest: s.removeBigPendingRequest,
  }));

  // Resolve exchange account ID: use the trade form's selected account if available,
  // otherwise find the user's account matching the campaign's exchange
  const accountId =
    tradeForm.selectedAccount?.value ??
    hubUser?.exchangeAccounts?.find((a) => campaign?.exchangeIds?.includes(a.exchangeId))?.id?.toString();

  const isEnabled =
    !!hubUser?.defiAddress &&
    !!campaignName &&
    authenticated &&
    !!auth?.identityToken &&
    !!campaign &&
    campaign.status === CAMPAIGN_STATUS.LIVE;

  const { data: validationResponse, isLoading } = useQuery({
    queryKey: [VALIDATE_HUB_DEFI_ADDRESS_QUERY_KEY, hubUser?.defiAddress, accountId, campaignName, campaign?.status],
    queryFn: async () => {
      try {
        addBigPendingRequest();
        const apiResponse = await apiHelper.postAuth(auth, API_ENDPOINT.VALIDATE_DEFI_ADDRESS, {
          bodyParams: {
            campaignName,
            exchangeAccountId: accountId,
          },
        });
        return {
          isValid: apiResponse.isValid,
          defiAddresses: apiResponse.defiAddresses ?? [],
          reason: apiResponse.reason,
        };
      } catch {
        return DEFAULT_VALIDATION_RESPONSE;
      } finally {
        removeBigPendingRequest();
      }
    },
    enabled: isEnabled,
    staleTime: 5_000,
  });

  /**
   * @remarks
   * the validation is always valid if the user is not authenticated
   */
  return {
    validationResponse: isEnabled ? validationResponse : DEFAULT_VALIDATION_RESPONSE,
    isLoading,
  };
}
